import React, { useMemo, useState } from "react";
import { usePatientContext } from "../../../../classes/contexts/PatientContext";
import { useUser } from "../../../../classes/contexts/UserContext";
import IconsEnum from "../../../../classes/enums/IconsEnum";
import { formatDisplayDate } from "../../../../classes/utils/DateUtils";
import {
  calculateBMI,
  convertHeight,
  convertWeight,
  formatHeightWithUnit,
  formatWeightWithUnit,
} from "../../../../classes/utils/HealthUtils";
import IconButton from "../../../../components/iconButton/IconButton";
import { useDateFormatter } from "../../../../hooks/useDateFormatter";
import useTranslation from "../../../../hooks/i18n/useTranslation";
import "./PatientInformation.css";

const PatientInformation = ({
  conditionsData,
  qualityOfLifeIndicators = [],
  weightValue = null,
  heightValue = null,
}) => {
  const { t } = useTranslation();
  const { patientData } = usePatientContext();
  const { userPreferences, dateSettings } = useUser();
  const [isBasicInfoExpanded, setIsBasicInfoExpanded] = useState(true);
  const [isClinicalHistoryExpanded, setIsClinicalHistoryExpanded] =
    useState(true);

  // Get user's preferred units (default to metric)
  const preferredWeightUnit =
    userPreferences?.display_settings?.weight_unit || "kg";
  const preferredLengthUnit =
    userPreferences?.display_settings?.length_unit || "cm";

  const normalizedWeightValue = useMemo(() => {
    const parsed = Number(weightValue);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [weightValue]);

  const normalizedHeightValue = useMemo(() => {
    const parsed = Number(heightValue);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [heightValue]);

  // Convert weight to user's preferred unit
  const displayWeight = useMemo(() => {
    if (normalizedWeightValue == null) return null;
    const converted = convertWeight(
      normalizedWeightValue,
      "kg",
      preferredWeightUnit
    );
    return formatWeightWithUnit(converted, preferredWeightUnit);
  }, [normalizedWeightValue, preferredWeightUnit]);

  // Convert height to user's preferred unit (from meters to cm or ft)
  const displayHeight = useMemo(() => {
    if (normalizedHeightValue == null) return null;
    const converted = convertHeight(
      normalizedHeightValue,
      "m",
      preferredLengthUnit
    );
    return formatHeightWithUnit(converted, preferredLengthUnit);
  }, [normalizedHeightValue, preferredLengthUnit]);

  // Calculate BMI using weight in kg and height in meters (BMI is always kg/m²)
  const bmi = useMemo(() => {
    const calculatedBMI = calculateBMI(
      normalizedWeightValue,
      normalizedHeightValue
    );
    return calculatedBMI;
  }, [normalizedWeightValue, normalizedHeightValue]);

  const parsedQualityOfLife = useMemo(
    () =>
      (qualityOfLifeIndicators || []).map(item => ({
        id: item.id || item._id,
        name: item.name || "QOL",
        value: item.value,
        max_value: item.max_value,
        date: item.date || item.createdAt,
      })),
    [qualityOfLifeIndicators]
  );

  return (
    <div className="PatientInformation-container">
      {/* Section 1: Basic Information */}
      <div className="PatientInformation-item">
        <div className="PatientInformation-header">
          <div className="App-text-xlarge">
            {patientData.first_name} {patientData.last_name}
          </div>
          <IconButton
            icon={isBasicInfoExpanded ? IconsEnum.ARROWUP : IconsEnum.ARROWDOWN}
            onClick={() => setIsBasicInfoExpanded(!isBasicInfoExpanded)}
            noBgColor
            title={isBasicInfoExpanded ? t("434") : t("433")}
          />
        </div>

        {patientData && isBasicInfoExpanded && (
          <div className="PatientInformation-body">
            <div className="PatientInformation-details">
              <div className="PatientInformation-detail-item App-text-small">
                <span className="PatientInformation-label">Birthday:</span>
                {formatDisplayDate(patientData.birthday, {
                  locale: dateSettings?.locale,
                  format: dateSettings?.format,
                })}
              </div>
              <div className="PatientInformation-detail-item App-text-small">
                <span className="PatientInformation-label">Sex:</span>
                {patientData.gender}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Section 2: Clinical History */}
      <div className="PatientInformation-item">
        <div className="PatientInformation-header">
          <div className="App-text-large">Clinical History</div>
          <IconButton
            icon={
              isClinicalHistoryExpanded
                ? IconsEnum.ARROWUP
                : IconsEnum.ARROWDOWN
            }
            onClick={() =>
              setIsClinicalHistoryExpanded(!isClinicalHistoryExpanded)
            }
            noBgColor
            title={isClinicalHistoryExpanded ? t("434") : t("433")}
          />
        </div>

        {isClinicalHistoryExpanded && (
          <div className="PatientInformation-body">
            <div className="PatientInformation-details">
              <div className="PatientInformation-detail-item App-text-small">
                <span className="PatientInformation-label">Weight:</span>
                {displayWeight || "N/A"}
              </div>
              <div className="PatientInformation-detail-item App-text-small">
                <span className="PatientInformation-label">Height:</span>
                {displayHeight || "N/A"}
              </div>
              <div className="PatientInformation-detail-item App-text-small">
                <span className="PatientInformation-label">BMI:</span>
                {bmi} {bmi !== "N/A" ? "kg/m²" : ""}
              </div>
              {conditionsData && conditionsData.length > 0 && (
                <div className="PatientInformation-conditions-section App-text-small">
                  <span className="PatientInformation-label">
                    Clinical Conditions:
                  </span>
                  <div className="PatientInformation-conditions-list">
                    {conditionsData.join(", ")}
                  </div>
                </div>
              )}
              {parsedQualityOfLife.length > 0 && (
                <div className="PatientInformation-quality-of-life">
                  <div className="PatientInformation-label App-text-small">
                    Quality of Life:
                  </div>
                  <div className="PatientInformation-quality-of-life-items">
                    {parsedQualityOfLife.map(item => (
                      <div
                        key={item.id}
                        className="PatientInformation-quality-of-life-item"
                      >
                        <div className="PatientInformation-quality-of-life-row">
                          <span className="PatientInformation-quality-of-life-label App-text-small">
                            {(item.name || "QOL").slice(0, 5)}
                          </span>
                          <span className="PatientInformation-quality-of-life-score-value App-text-small">
                            <span className="score-value">
                              {item.value ?? "N/A"}
                            </span>
                            {item.max_value ? (
                              <>
                                <span className="score-separator">/</span>
                                <span className="score-total">
                                  {item.max_value}
                                </span>
                              </>
                            ) : null}
                          </span>
                        </div>
                        {item.date && (
                          <div className="PatientInformation-quality-of-life-date App-text-small">
                            {formatDisplayDate(item.date, {
                              locale: dateSettings?.locale,
                              format: dateSettings?.format,
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(PatientInformation);
